import { Text, VSpacer } from '@/components/DesignSystem';
import { useAuthentication } from '@/contexts/dataSync/AuthenticationContext';
import { AuthApi } from '@/utilities/api/AuthApi';
import { Alert, Box, Button, Stack } from '@mui/material';
import React, { useState } from 'react';
import BlackFullLogo from '@/assets/logos/black/app_full_black.svg';
import { validatePassword } from '@shared/utilities';
import { useTranslation } from 'react-i18next';
import { PasswordInputFields } from './PasswordInputFields';

export const ForcePasswordChangePage = () => {
  const { t } = useTranslation();
  const { refreshUser } = useAuthentication();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError(t($ => $.auth.passwordReset.passwordsDoNotMatch));
      return;
    }

    if (!validatePassword(newPassword)) {
      setError(t($ => $.auth.passwordReset.passwordRequirementsNotMet));
      return;
    }

    setLoading(true);
    try {
      await AuthApi.changePassword({ newPassword });
      await refreshUser();
    } catch (err) {
      setError(err instanceof Error ? err.message : t($ => $.auth.passwordReset.genericError));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack
      alignItems="center"
      direction="row"
      justifyContent="center"
      paddingTop="100px"
    >
      <Stack display="flex" flex={1} maxWidth="512px">
        <Stack
          alignItems="center"
          borderRadius="24px"
          boxShadow={
            '0px 1px 2px 0px rgba(0, 0, 0, 0.07),'
            + '0px 2px 4px 0px rgba(0, 0, 0, 0.07),'
            + '0px 4px 8px 0px rgba(0, 0, 0, 0.07),'
            + '0px 8px 16px 0px rgba(0, 0, 0, 0.07),'
            + '0px 16px 32px 0px rgba(0, 0, 0, 0.07),'
            + '0px 32px 64px 0px rgba(0, 0, 0, 0.07)'
          }
          padding="60px 24px 40px 24px"
          width="560px"
        >
          <img
            alt="logo"
            src={BlackFullLogo}
            style={{ height: '40px', width: '205px', objectFit: 'contain' }}
          />
          <VSpacer size="8" />
          <Text category="headline-small" sx={{ textAlign: 'center' }}>
            {t($ => $.auth.passwordReset.forceChangeTitle)}
          </Text>
          <Text category="body-medium" sx={{ textAlign: 'center', color: 'text.secondary' }}>
            {t($ => $.auth.passwordReset.forceChangeSubtitle)}
          </Text>
          <VSpacer size="4" />
          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <PasswordInputFields
              confirmPassword={confirmPassword}
              disabled={loading}
              newPassword={newPassword}
              onConfirmPasswordChange={setConfirmPassword}
              onNewPasswordChange={setNewPassword}
            />
            <Button
              disabled={loading || !newPassword || !confirmPassword}
              fullWidth
              size="large"
              sx={{ mt: 3 }}
              type="submit"
              variant="contained"
            >
              {loading
                ? t($ => $.auth.passwordReset.submittingChange)
                : t($ => $.auth.passwordReset.submitChange)}
            </Button>
          </Box>
        </Stack>
      </Stack>
    </Stack>
  );
};
